<template>
  <div class="ml-2 odds-selector-wrapper">
    <transition name="snap-fade">
      <div v-if="snapMessage" class="snap-notice">
        {{ snapMessage }}
      </div>
    </transition>
    <button
      class="oddsButton bottom"
      tabindex="0"
      @click="emitIncrementOrDesc('+')"
    >
      +
    </button>
    <input
      id="odds"
      v-model="oddsComputed"
      name="odds"
      :min="oddsFormat === 'decimal' ? -2000 : 0"
      max="2000"
      class="odds-input d-inline-flex text-center bet-modal-input"
      placeholder="odds"
      :maxlength="oddsComputed.toString().includes('-') ? 7 : 6"
      type="text"
      @keyup="emitUpdateOdds"
      @input="variable = $event.target.value"
      @blur="snapOddsOnBlur"
    />
    <button
      class="oddsButton top"
      tabindex="0"
      @click="emitIncrementOrDesc('-')"
    >
      -
    </button>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { useUserStore } from '@/stores/user';
import { useExchangeStore } from '@/stores/exchange';
import {
  convertAmericanToDecimal,
  convertDecimalToAmerican,
  convertImpliedProbabilityToAmerican,
  convertAmericanToImpliedProbability,
  formatDecimalOddsForInput,
  roundTo3,
} from '@/core/utils';
import {
  getAmericanTickInfo,
  getDecimalTickInfo,
  getIPTickInfo,
} from '@/core/ticks';

const props = defineProps({
  odds: {
    type: Number,
    required: true,
  },
  oddsKey: {
    type: Number,
    required: false,
    default: 0,
  },
  editOrderMode: {
    type: Boolean,
    required: false,
    default: false,
  },
  isPercentage: {
    type: Boolean,
    required: false,
    default: false,
  },
});

const emit = defineEmits(['updateOdds']);

const userStore = useUserStore();
const exchangeStore = useExchangeStore();

const variable = ref('');
const snapMessage = ref('');
let snapMessageTimeout = null;

const oddsFormat = computed(() => {
  return userStore.user.oddsFormat;
});

const oddsComputed = computed({
  get() {
    return exchangeStore.oddsComputed;
  },
  set(newValue) {
    const val = newValue === -100 ? 100 : newValue;
    exchangeStore.setOddsComputed(roundTo3(val));
  },
});

function displayToAmerican(displayValue) {
  if (oddsFormat.value === 'decimal') return convertDecimalToAmerican(displayValue);
  if (oddsFormat.value === 'impliedProbability') return convertImpliedProbabilityToAmerican(displayValue);
  return displayValue;
}

function americanToDisplay(american) {
  if (oddsFormat.value === 'decimal') return parseFloat(formatDecimalOddsForInput(convertAmericanToDecimal(american)));
  if (oddsFormat.value === 'impliedProbability') return convertAmericanToImpliedProbability(american);
  return american;
}

function showSnapNotice(snappedAmerican) {
  const sign = snappedAmerican >= 0 ? '+' : '';
  switch (oddsFormat.value) {
    case 'decimal':
      snapMessage.value = `Odds adjusted to ${formatDecimalOddsForInput(convertAmericanToDecimal(snappedAmerican))}`;
      break;
    case 'impliedProbability':
      snapMessage.value = `Odds adjusted to ${convertAmericanToImpliedProbability(snappedAmerican).toFixed(1)}%`;
      break;
    default:
      snapMessage.value = `Odds adjusted to ${sign}${snappedAmerican}`;
  }
  clearTimeout(snapMessageTimeout);
  snapMessageTimeout = setTimeout(() => { snapMessage.value = ''; }, 3000);
}

function snapOddsOnBlur() {
  if (props.isPercentage) return;
  const val = parseFloat(oddsComputed.value);
  if (isNaN(val)) return;

  if (oddsFormat.value === 'decimal') {
    const snapped = getDecimalTickInfo(val).snap;
    const american = convertDecimalToAmerican(snapped);
    if (snapped !== val) showSnapNotice(american);
    oddsComputed.value = formatDecimalOddsForInput(snapped);
    emit('updateOdds', american);
    return;
  }

  if (oddsFormat.value === 'impliedProbability') {
    const snapped = getIPTickInfo(val).snap;
    const american = convertImpliedProbabilityToAmerican(snapped);
    if (Math.abs(snapped - val) > 0.05) showSnapNotice(american);
    oddsComputed.value = snapped;
    emit('updateOdds', american);
    return;
  }

  const snapped = getAmericanTickInfo(val).snap;
  const display = americanToDisplay(snapped);
  if (display !== val) showSnapNotice(snapped);
  oddsComputed.value = display;
  emit('updateOdds', snapped);
}

function emitUpdateOdds() {
  if (oddsFormat.value === 'decimal') {
    if (
      !Number.isNaN(Number(oddsComputed.value)) &&
      Number(oddsComputed.value) < 1
    ) {
      oddsComputed.value = '';
    }
  } else if (props.isPercentage) {
    const percentValue = parseFloat(oddsComputed.value);
    if (percentValue < 0.1) oddsComputed.value = 0.1;
    if (percentValue > 99.99) oddsComputed.value = 99.99;
  } else if (oddsFormat.value === 'impliedProbability') {
    const impliedValue = parseFloat(oddsComputed.value);
    if (impliedValue < 0) oddsComputed.value = 0;
    if (impliedValue > 100) oddsComputed.value = 100;
  }
  let chain = oddsComputed.value.toString();
  const appearances = (chain.match(/-/g) || []).length;
  if (appearances > 1) {
    const posLast = chain.lastIndexOf('-');
    chain = chain.slice(0, posLast - 1) + chain.slice(posLast, chain.length);
    oddsComputed.value = '';
  }
  if (oddsFormat.value === 'decimal') {
    emit('updateOdds', convertDecimalToAmerican(oddsComputed.value).toFixed(3));
  } else if (oddsFormat.value === 'impliedProbability') {
    emit('updateOdds', convertImpliedProbabilityToAmerican(oddsComputed.value));
  } else {
    emit('updateOdds', oddsComputed.value);
  }
}

function emitIncrementOrDesc(sign) {
  oddsComputed.value = parseFloat(oddsComputed.value);

  if (props.isPercentage) {
    if (sign === '+') {
      oddsComputed.value = Math.min(99.99, oddsComputed.value + 0.1);
    } else {
      oddsComputed.value = Math.max(0.1, oddsComputed.value - 0.1);
    }
    oddsComputed.value = parseFloat(oddsComputed.value).toFixed(2);
    emit('updateOdds', oddsComputed.value);
    return;
  }

  const wantsHigher =
    (!props.editOrderMode && sign === '+') ||
    (props.editOrderMode && sign === '-');

  if (oddsFormat.value === 'impliedProbability') {
    const ip = parseFloat(oddsComputed.value);
    const tick = getIPTickInfo(ip);
    const newIP = wantsHigher ? tick.next : tick.prev;
    oddsComputed.value = newIP;
    emit('updateOdds', convertImpliedProbabilityToAmerican(newIP));
    return;
  }

  if (oddsFormat.value === 'decimal') {
    const dec = parseFloat(oddsComputed.value);
    const tick = getDecimalTickInfo(dec);
    const newDec = wantsHigher ? tick.next : tick.prev;
    oddsComputed.value = formatDecimalOddsForInput(newDec);
    emit('updateOdds', convertDecimalToAmerican(newDec));
    return;
  }

  const american = parseFloat(oddsComputed.value);
  const abs = Math.abs(american);
  const step = abs < 200 ? 1
    : abs < 1000 ? Math.floor(abs / 100)
    : 25;
  let newAmerican = wantsHigher ? american + step : american - step;
  if (american < 0 && newAmerican >= -100) newAmerican = 100;
  if (american >= 100 && newAmerican < 100) newAmerican = -101;
  oddsComputed.value = newAmerican;
  emit('updateOdds', newAmerican);
}

onMounted(() => {
  if (oddsFormat.value === 'decimal') {
    oddsComputed.value = formatDecimalOddsForInput(convertAmericanToDecimal(props.odds));
    emit('updateOdds', props.odds);
  } else if (props.isPercentage) {
    oddsComputed.value = props.odds.toFixed(2);
  } else if (
    props.odds.toString().includes('.') &&
    oddsFormat.value !== 'impliedProbability'
  ) {
    oddsComputed.value = !props.odds.toFixed(1).includes('.0')
      ? props.odds.toFixed(1)
      : Math.floor(props.odds);
  } else if (oddsFormat.value === 'impliedProbability') {
    oddsComputed.value = Math.round(convertAmericanToImpliedProbability(props.odds));
    emit('updateOdds', props.odds);
  } else {
    oddsComputed.value = props.odds;
    emit('updateOdds', oddsComputed.value);
  }
});
</script>

<style scoped lang="scss">
@use '@/scss/main.scss';

input {
  font-size: 16px;
  padding-left: 0rem;
  padding-right: 0rem;

  &:focus {
    border-bottom-color: main.$blue-403;
  }
}

.odds-input {
  width: 4rem;
}

input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

/* Firefox */
input[type='number'] {
  -moz-appearance: textfield;
}

.bet-modal-input {
  border-radius: 0px 0px 0px 0px;
  background-color: main.$white_02;
  color: main.$color-primary_01;
  border: 1px solid main.$color-primary_05;
  border-right: main.$transparent;
  border-left: main.$transparent;
  font-size: 18px;
  height: 44px;
  font-weight: 600;
}

.oddsButton {
  text-align: center;
  padding: 0rem 0.5rem;
  font-size: 18px;
  height: 44px;
  width: 30px;
  border: 1px solid main.$color-primary_05;
  background-color: main.$grey-1100;
  color: main.$color-primary_01;
  font-weight: bold;
  &:hover {
    background-color: main.$white_04;
  }

  &.top {
    border-radius: 0px 6px 6px 0px;
  }

  &.bottom {
    border-radius: 6px 0px 0px 6px;
    font-size: 21px !important;
  }
}

::placeholder {
  font-weight: lighter;
  font-size: main.$font-size-xs;
}

.odds-new {
  margin-left: 0 !important;
  width: 100%;

  input.odds-input {
    width: 100%;
    padding: 8px 1rem;
    font-weight: main.$font-weight-bold;
  }
}

.odds-selector-wrapper {
  position: relative;
}

.snap-notice {
  position: absolute;
  bottom: 100%;
  left: 50%;
  transform: translateX(-50%);
  margin-bottom: 6px;
  white-space: nowrap;
  padding: 4px 10px;
  font-size: 12px;
  font-weight: 600;
  color: main.$white;
  background: main.$blue-800_02;
  border-radius: 6px;
  pointer-events: none;
}

.snap-fade-enter-active {
  transition: opacity 0.2s ease;
}

.snap-fade-leave-active {
  transition: opacity 0.4s ease;
}

.snap-fade-enter-from,
.snap-fade-leave-to {
  opacity: 0;
}
</style>
